import React, { FormEvent, useState, useEffect } from 'react';
import { Button, Checkbox, Modal } from '@scuf/common';
import './TermsAndConditions.scss';
import i18next from '../../../i18next';
import { IUserProfile } from '../../../models';
import AgreementModal from './AgreementModal';
import { IAPIPackage } from '../../commonUtils/APIContext';
import { LanguageCodes } from '../../../locales/LanguageCodeObj';

interface IFormStringField {
  value: string | undefined;
  touched: boolean;
}

interface IAcceptForm {
  eulaAcceptedVersion: IFormStringField;
  privacyAcceptedVersion: IFormStringField;
}

interface ITermsAndConditionsProps extends IAPIPackage {
  userProfile: IUserProfile | null;
}

export const TermsAndConditions = (props: ITermsAndConditionsProps) => {
  const { userProfile } = props;

  const [eula, setEula] = useState<boolean>(false);
  const [privacy, setPrivacy] = useState<boolean>(false);
  const [eulaDisable, setEulaDisable] = useState<boolean>(true);
  const [privacyDisable, setPrivacyDisable] = useState<boolean>(true);
  const [isEulaModalVisible, setEulaModalVisibility] = useState<boolean>(false);
  const [isPrivacyModalVisible, setPrivacyModalVisibility] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalVisible, setModalVisibility] = useState<boolean>(false);
  const [languageCode, setLanguageCode] = useState<string>(LanguageCodes.enUS);
  const [formDataFields, setFormDataFields] = useState<IAcceptForm>({
    eulaAcceptedVersion: { value: '', touched: false },
    privacyAcceptedVersion: { value: '', touched: false }
  });

  useEffect(() => {
    setLanguageCode(i18next.language);
  }, []);

  useEffect(() => {
    if (userProfile && (userProfile.dataPrivacyAcceptedVersion !== '1' || userProfile.eulaAcceptedVersion !== '1')) {
      setModalVisibility(true);
    }
  }, [userProfile]);

  const newFormVal = (val: string) => {
    return { value: val, touched: true };
  };

  const handleSubmitForm = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (eula && privacy && formDataFields.eulaAcceptedVersion.value && formDataFields.privacyAcceptedVersion.value) {
      setIsLoading(true);
      const payload = {
        ...props.userProfile,
        eulaAcceptedVersion: formDataFields.eulaAcceptedVersion.value,
        dataPrivacyAcceptedVersion: formDataFields.privacyAcceptedVersion.value,
      } as IUserProfile;

      props.authFetch(
        `${props.baseURL}/api/v1/users/me`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      ).catch(() => {
        console.error('Couldnt update user profile');
      }).finally(() => {
        setIsLoading(false);
        setModalVisibility(false);
      });
    }
  };

  const onCloseEULAModal = () => {
    setEulaDisable(false);
    setEulaModalVisibility(true);
  };

  const onClosePrivacyModal = () => {
    setPrivacyDisable(false);
    setPrivacyModalVisibility(true);
  };

  return (
    <Modal className='terms-and-cond-modal' size='small' open={isModalVisible} closeIcon={false}>
      {isEulaModalVisible && (
        <AgreementModal
          languageCode={languageCode}
          title={i18next.t('endUser')}
          type='EULA'
          onClose={(version: string) => {
            setEulaModalVisibility(false);
            setFormDataFields(prevState => {
              return { ...prevState, eulaAcceptedVersion: newFormVal(version) };
            });
          }}
        />
      )}
      {isPrivacyModalVisible && (
        <AgreementModal
          languageCode={languageCode}
          title={`Honeywell ${i18next.t('privacyPolicy')}`}
          type='Privacy'
          onClose={(version: string) => {
            setPrivacyModalVisibility(false);
            setFormDataFields(prevState => {
              return {
                ...prevState,
                privacyAcceptedVersion: newFormVal(version)
              };
            });
          }}
        />
      )}
      <div className='terms-and-cond-modal-content'>
        <div className='terms-and-cond-modal-content-header'>{i18next.t('termsAndConditions')}</div>
        <form
          id='terms-and-cond-modal-content-form'
          className='terms-and-cond-modal-content-form'
          method='post'
          onSubmit={handleSubmitForm}
        >
          <div className='terms-and-cond-modal-content-form-agreement'>
            <Checkbox
              disabled={eulaDisable}
              className='terms-and-cond-modal-content-form-agreement-checkbox'
              checked={eula}
              onChange={checked => setEula(checked)}
            />
            <span className={`terms-and-cond-modal-content-form-agreement-label  ${eulaDisable ? 'disabled' : ''}`}>
              {i18next.t('iAccept')}
            </span>
            <span className={`terms-and-cond-modal-content-form-agreement-label  ${eulaDisable ? 'disabled' : ''}`}>
              {i18next.t('iHereby')}
              <span className='terms-and-cond-modal-content-form-agreement-label-link' onClick={onCloseEULAModal}>{` ${i18next.t('eula')}`}</span>
              {'.'}
            </span>
          </div>
          <div className='terms-and-cond-modal-content-form-agreement'>
            <Checkbox
              disabled={privacyDisable}
              className='terms-and-cond-modal-content-form-agreement-checkbox'
              checked={privacy}
              onChange={checked => setPrivacy(checked)}
            />
            <span className={`terms-and-cond-modal-content-form-agreement-label  ${privacyDisable ? 'disabled' : ''}`}>
              {i18next.t('iRead')}
            </span>
            <span className={`terms-and-cond-modal-content-form-agreement-label  ${privacyDisable ? 'disabled' : ''}`}>
              {i18next.t('iUnderstood')}
              <span className='terms-and-cond-modal-content-form-agreement-label-link' onClick={onClosePrivacyModal}>{` ${i18next.t('privacyPolicy')}`}</span>
            </span>
          </div>
          <Button
            actionType='submit'
            loading={isLoading}
            disabled={!eula || !privacy}
            className='terms-and-cond-modal-content-form-accept-button'
          >
            {i18next.t('accept')}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default TermsAndConditions;